import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from '../Link';
import { useMyJobsBadgeCount } from '../../hooks/useMyJobsBadgeCount';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { Msg } from '../../i18n-review/client/Msg';

/**
 * Persistent "My Jobs" header entry: renders on every page (no more `isToolsPage()` gate), with
 * a live count badge, replacing the 10-tab ToolSwitcher strip that only appeared once you were
 * already on a tools page. See SEEKER_HUB_PLAN.md §2.2 and P1 item 7.
 *
 * Height/hover-press feel matches the rest of the nav row (52px, subtle scale on hover/press):
 * shared by NavigationNavbar.tsx (every non-SRP page) and the SRP header's own nav row, so this
 * one component is what keeps them looking the same rather than two copies drifting apart.
 */
export const MyJobsNavEntry: React.FC<{ locale: string; className?: string }> = ({ locale, className = '' }) => {
  const count = useMyJobsBadgeCount();
  return (
    <Link
      href="/tools"
      locale={normalizeSupportedLocale(locale)}
      className={`inline-flex items-center gap-1.5 px-4 h-[52px] rounded-full text-sm font-semibold text-jc-ink-secondary border border-jc-border bg-jc-surface/90 hover:bg-jc-elevated hover:text-jc-ink hover:scale-[1.02] active:scale-[0.97] transition-[background-color,color,transform] duration-150 whitespace-nowrap ${className}`}
    >
      <Msg id="hub.nav.myJobs" defaultMessage="My Jobs" description="Persistent header entry linking to the unified My Jobs hub" />
      {count > 0 ? (
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-jc-accent text-white text-[11px] font-bold tabular-nums">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
};
